  import React, { useState, useEffect } from "react";
import { Save, ShieldCheck, Zap, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const TelegramConfig = ({ activeAgentId }) => {
  const { showToast } = useToast();
  const [telegramConfig, setTelegramConfig] = useState({
    bot_token: "",
    is_active: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [activeAgentId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.request("/telegram/config");
      if (response.success && response.config) {
        setTelegramConfig({
          bot_token: response.config.bot_token ? UNCHANGED_KEY : "",
          is_active: response.config.is_active || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.log("Telegram config not found");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!telegramConfig.bot_token.trim()) {
      showToast("Please enter your Bot Token", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/telegram/config", {
        method: "PUT",
        body: JSON.stringify(telegramConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Telegram configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    try {
      const newActiveState = !telegramConfig.is_active;
      const response = await ApiClient.request("/telegram/config", {
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setTelegramConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };



  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Telegram Sync"
          description="Dispatch neural intelligence directly to your users' chat stream via the Telegram bot protocol."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${telegramConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {telegramConfig.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={telegramConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-12 h-7 bg-gray-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-200 after:border after:rounded-full after:h-[19px] after:w-[19px] after:transition-all peer-checked:bg-[#83bbe1] shadow-inner"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Bot Credentials</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Telegram bot</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Bot Token
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : telegramConfig.bot_token}
                    onChange={(e) =>
                      setTelegramConfig({
                        ...telegramConfig,
                        bot_token: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (telegramConfig.bot_token === UNCHANGED_KEY) {
                        setTelegramConfig({ ...telegramConfig, bot_token: "" });
                      }
                    }}
                    placeholder={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : "Enter your Telegram Bot Token"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
                <div className="mt-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 flex items-start gap-3">
                  <div className="mt-0.5 text-blue-500">
                    <Globe size={16} />
                  </div>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    You can obtain this token from <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-blue-800">@BotFather</a> on Telegram. Send the <code>/newbot</code> command to create a new bot.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-end">
                <button
                  onClick={saveConfig}
                  disabled={saving || !telegramConfig.bot_token?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm"
                >
                  <Save size={18} />
                  {saving ? "Refining..." : "Save Bot Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Endpoint</h3>
                  <p className="text-[11px] font-medium text-gray-400">Real-time message delivery endpoint</p>
                </div>
              </div>

              <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Live Webhook URL
                </label>
                <div className="flex items-center justify-between gap-4 font-mono text-xs text-gray-600 truncate">
                  <span className="truncate">{webhookUrl}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-400">
                This URL is automatically registered with Telegram's API upon saving your configuration.
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TelegramConfig;
